import { confirmUserAttribute } from '@aws-amplify/auth';
import api from '@common/api';
import { InvestorAccountModel } from '@common/models/InvestorAccountModel';
import create from 'zustand';
import { Address } from '../types';

type APIResult = Promise<{ success: boolean; error?: string }>;

type AccountInfoState = 'LOADING' | 'RENDER';
interface AccountInfoViewModelProps {
  id?: string;
}
interface AccountInfoViewModelActions {
  confirmPhoneNumber: (code: string, number: string) => APIResult;
  setState: (state: AccountInfoState) => void;
  updateAddress: (address: Address) => APIResult;
  updateEmailAddress: (email: string) => APIResult;
  updatePhoneNumber: (number: string) => APIResult;
}
interface AccountInfoViewModelProps {
  state: AccountInfoState;
}

export const useAccountInfoViewModel = create<AccountInfoViewModelProps & AccountInfoViewModelActions>((set) => {
  return {
    confirmPhoneNumber: async (code: string, number: string) => {
      const investorID = InvestorAccountModel.getState().investorAccount?.id;
      if (!investorID) return { error: 'No investor ID', success: false };

      try {
        await confirmUserAttribute({ confirmationCode: code, userAttributeKey: 'phone_number' });
        await api.confirmPhoneNumberUpdate({ investor_account_id: investorID, phone_number: number });
        await InvestorAccountModel.getState().fetchInvestorAccount(); // refresh investor account
        return { success: true };
      } catch (err) {
        return { error: 'Unable to verify phone number', success: false };
      }
    },
    setState: (state: AccountInfoState) => {
      set({ state });
    },

    state: 'LOADING',
    updateAddress: async (address: Address) => {
      const investorID = InvestorAccountModel.getState().investorAccount?.id;
      if (!investorID) return { error: 'No investor ID', success: false };
      const payload = {
        investor_account: {
          address: address.address1.trim(),
          address2: address.address2 || '',
          city: address.city.trim(),
          country: 'US',
          state: address.state.trim(),
          zip: address.zip.trim(),
        },
        investor_account_id: investorID,
      };

      try {
        await api.updateInvestorAccount(payload);
        await InvestorAccountModel.getState().fetchInvestorAccount(); // refresh investor account
        return { success: true };
      } catch {
        return { error: 'Unable to update address', success: false };
      }
    },
    updateEmailAddress: async (email: string) => {
      const investorID = InvestorAccountModel.getState().investorAccount?.id;
      if (!investorID) return { error: 'No investor ID', success: false };

      try {
        if (!(await api.isEmailAvailable({ email }))) {
          return { error: 'Email is already in use. Please use a unique email', success: false };
        }
        await api.updateInvestorAccount({ investor_account: { email }, investor_account_id: investorID });
        await InvestorAccountModel.getState().fetchInvestorAccount(); // refresh investor account
        return { success: true };
      } catch {
        return { error: 'Unable to update email address', success: false };
      }
    },
    updatePhoneNumber: async (number: string) => {
      const investorID = InvestorAccountModel.getState().investorAccount?.id;
      if (!investorID) return { error: 'No investor ID', success: false };
      try {
        await api.updateInvestorAccount({ investor_account: { phone: number }, investor_account_id: investorID });
        await InvestorAccountModel.getState().fetchInvestorAccount(); // refresh investor account
        return { success: true };
      } catch {
        return { error: 'Unable to update phone number', success: false };
      }
    },
  };
});
